'use client'
import { useFormatter, useTranslations } from 'next-intl'
import { Loader2 } from 'lucide-react'
import type { knowledgeSourceConnections } from '@/drizzle/schema'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { TranslationText } from '@/components/i18n/translation-text'
import { KNOWLEDGE_SOURCE_PROVIDERS } from '@/lib/knowledge-sources/providers'
import { KnowledgeSourceProviderIcon } from './provider-icon'

type Connection = Pick<typeof knowledgeSourceConnections.$inferSelect, 'id' | 'provider' | 'accountLabel' | 'status' | 'lastPolledAt' | 'lastError'>

export function KnowledgeSourceConnectionCard({ connection, canManage, onReconnect, onDisconnect }: {
  connection: Connection; canManage: boolean; onReconnect(): void; onDisconnect(): void;
}) {
  const t = useTranslations('KnowledgeSources')
  const format = useFormatter()
  const status = connectionStatus(connection.status, t)
  const provider = KNOWLEDGE_SOURCE_PROVIDERS.find(provider => provider.id === connection.provider)
  return <div className="flex items-start gap-3 rounded-lg border bg-background p-4 sm:gap-4">
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border bg-muted/30">
      <KnowledgeSourceProviderIcon provider={connection.provider} className="h-5 w-5" />
    </div>
    <div className="min-w-0 flex-1 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <p className="min-w-0 break-all font-medium leading-6"><span key={connection.accountLabel}>{connection.accountLabel}</span></p>
        <Badge variant={status.variant} className="max-w-full gap-1.5 py-1">
          {connection.status === 'disconnecting' && <Loader2 aria-hidden="true" className="h-3 w-3 shrink-0 animate-spin" />}
          <span key={connection.status}>{status.label}</span>
        </Badge>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
        <span>{provider?.name ?? connection.provider}</span>
        <span aria-hidden="true" className="h-3 w-px bg-border" />
        <p className="min-w-0 break-words">
          <TranslationText>{connection.lastPolledAt ? t('lastChecked', { date: format.dateTime(new Date(connection.lastPolledAt), { dateStyle: 'short', timeStyle: 'short' }) }) : t('notChecked')}</TranslationText>
        </p>
      </div>
      {connection.lastError && <p className="text-sm text-destructive"><span>{t('connectionAttention')}</span></p>}
      {connection.status !== 'disconnecting' && canManage && <div className="flex flex-wrap gap-2 pt-1">
        {connection.status === 'reconnect_required' && <Button variant="outline" size="sm" onClick={onReconnect}><span>{t('reconnect')}</span></Button>}
        <Button variant="ghost" size="sm" onClick={onDisconnect}><span>{t('disconnect')}</span></Button>
      </div>}
    </div>
  </div>
}

function connectionStatus(status: Connection['status'], t: ReturnType<typeof useTranslations<'KnowledgeSources'>>): { variant: BadgeProps['variant']; label: string } {
  switch (status) {
    case 'connected': return { variant: 'secondary', label: t('connectionConnected') }
    case 'reconnect_required': return { variant: 'destructive', label: t('connectionReconnect') }
    case 'disconnecting': return { variant: 'outline', label: t('connectionDisconnecting') }
  }
}
